import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2, Check, AlertCircle, Plug, Plus, X } from 'lucide-react';
import { createConnector, updateConnector } from '@/lib/connector-api';
import type { Connector } from '@/lib/connector-types';

interface ConnectorConfigFormProps {
  connector?: Connector | null;
  workspaceId: string | null;
  onBack: () => void;
  onCreated: () => void;
}

interface HeaderEntry {
  key: string;
  value: string;
}

function parseExistingHeaders(raw: unknown): HeaderEntry[] {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as Record<string, string>;
      return Object.entries(parsed).map(([key, value]) => ({ key, value }));
    } catch {
      return [];
    }
  }
  if (typeof raw !== 'object') return [];
  return Object.entries(raw as Record<string, string>).map(([key, value]) => ({ key, value }));
}

function getHeaderRecord(raw: unknown): Record<string, string> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, string>;
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') {
    return raw as Record<string, string>;
  }
  return {};
}

function inferAuthFromHeaders(headers: Record<string, string>) {
  const keys = Object.keys(headers);
  if (keys.length === 1 && headers['X-API-Key']) {
    return { authType: 'api_key', apiKey: headers['X-API-Key'] } as const;
  }
  if (keys.length === 1 && headers['Authorization']?.startsWith('Bearer ')) {
    return { authType: 'bearer', apiKey: headers['Authorization'].replace(/^Bearer\s+/i, '') } as const;
  }
  if (keys.length > 0) {
    return { authType: 'custom_headers', apiKey: '' } as const;
  }
  return { authType: 'none', apiKey: '' } as const;
}

export function ConnectorConfigForm({ connector, workspaceId, onBack, onCreated }: ConnectorConfigFormProps) {
  const isEditing = !!connector;
  const cfg = (connector?.config ?? {}) as Record<string, unknown>;
  const headersSource = (connector?.headers as Record<string, string> | undefined)
    ?? (cfg.headers as Record<string, string> | undefined)
    ?? (cfg.headers as unknown);
  const inferredAuth = inferAuthFromHeaders(getHeaderRecord(headersSource));

  const [name, setName] = useState(connector?.name ?? '');
  const [description, setDescription] = useState(connector?.description ?? '');
  const [baseUrl, setBaseUrl] = useState(
    (connector?.baseUrl as string) ??
      (cfg.baseUrl as string) ??
      (cfg.base_url as string) ??
      '',
  );
  const [apiKey, setApiKey] = useState(inferredAuth.apiKey ?? (cfg.apiKey as string) ?? '');
  const [authType, setAuthType] = useState((cfg.authType as string) ?? inferredAuth.authType ?? 'none');
  const [customHeaders, setCustomHeaders] = useState<HeaderEntry[]>(
    () => parseExistingHeaders(headersSource)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addHeader = () => {
    setCustomHeaders([...customHeaders, { key: '', value: '' }]);
  };

  const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
    const updated = [...customHeaders];
    updated[index] = { ...updated[index], [field]: val };
    setCustomHeaders(updated);
  };

  const removeHeader = (index: number) => {
    setCustomHeaders(customHeaders.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!baseUrl.trim()) {
      setError('Server URL is required');
      return;
    }
    if (authType === 'custom_headers') {
      const validHeaders = customHeaders.filter(h => h.key.trim());
      if (validHeaders.length === 0) {
        setError('Add at least one header with a name');
        return;
      }
    }

    if (!workspaceId) {
      setError('Workspace not available');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Build headers from auth type
      const headers: Record<string, string> = {};

      if (authType === 'custom_headers') {
        for (const h of customHeaders) {
          if (h.key.trim()) headers[h.key.trim()] = h.value;
        }
      } else if (authType === 'api_key' && apiKey) {
        headers['X-API-Key'] = apiKey;
      } else if (authType === 'bearer' && apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      if (isEditing) {
        await updateConnector(connector!.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          baseUrl: baseUrl.trim(),
          ...(Object.keys(headers).length > 0 ? { headers } : {}),
        });
      } else {
        // Backend schema: { name, baseUrl, workspaceId, authType: 'none'|'api_key'|'oauth2', headers? }
        // We use authType 'none' and pass credentials via headers for simplicity
        await createConnector({
          name: name.trim(),
          baseUrl: baseUrl.trim(),
          workspaceId,
          authType: 'none',
          description: description.trim() || undefined,
          ...(Object.keys(headers).length > 0 ? { headers } : {}),
        });
      }

      onCreated();
    } catch (err: any) {
      setError(err.message || `Failed to ${isEditing ? 'update' : 'create'} connector`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b">
        <div className="flex items-center gap-2.5">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-7 w-7 p-0 shrink-0">
            <ArrowLeft className="size-4" />
          </Button>
          <div className="flex items-center justify-center size-8 rounded-lg bg-primary/10">
            <Plug className="size-4 text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold leading-tight">
              {isEditing ? 'Edit Connector' : 'Custom Connector'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {isEditing ? 'Update connector settings' : 'Connect to an MCP server'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {error && (
          <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-md border border-destructive/20">
            <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Basic info */}
        <div className="space-y-3">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Basic Info</p>
          <div className="rounded-md border bg-card overflow-hidden">
            <div className="px-3 py-3 border-b space-y-1.5">
              <Label htmlFor="name" className="text-xs font-medium">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My MCP Server"
                className="text-xs h-8"
              />
            </div>
            <div className="px-3 py-3 space-y-1.5">
              <Label htmlFor="description" className="text-xs font-medium">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What does this connector do?"
                className="text-xs h-8"
              />
            </div>
          </div>
        </div>

        {/* Server config */}
        <div className="space-y-3">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Server Configuration</p>
          <div className="rounded-md border bg-card overflow-hidden">
            <div className="px-3 py-3 border-b space-y-1.5">
              <Label htmlFor="baseUrl" className="text-xs font-medium">Server URL</Label>
              <Input
                id="baseUrl"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://mcp.example.com/mcp"
                className="text-xs h-8 font-mono"
              />
              <p className="text-[10px] text-muted-foreground">
                The MCP server endpoint. Tools will be auto-discovered.
              </p>
            </div>
            <div className="px-3 py-3 space-y-1.5">
              <Label htmlFor="authType" className="text-xs font-medium">Authentication</Label>
              <select
                id="authType"
                value={authType}
                onChange={(e) => {
                  const val = e.target.value;
                  setAuthType(val);
                  if (val === 'custom_headers' && customHeaders.length === 0) {
                    setCustomHeaders([{ key: '', value: '' }]);
                  }
                }}
                className="w-full h-8 px-3 rounded-md border border-input bg-background text-xs"
              >
                <option value="none">None</option>
                <option value="api_key">API Key</option>
                <option value="bearer">Bearer Token</option>
                <option value="custom_headers">Custom Headers</option>
              </select>
            </div>

            {/* API Key / Bearer Token field */}
            {(authType === 'api_key' || authType === 'bearer') && (
              <div className="px-3 py-3 border-t space-y-1.5">
                <Label htmlFor="apiKey" className="text-xs font-medium">
                  {authType === 'bearer' ? 'Bearer Token' : 'API Key'}
                </Label>
                <Input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={authType === 'bearer' ? 'Bearer token...' : 'API key...'}
                  className="text-xs h-8 font-mono"
                />
                {isEditing && !apiKey && (
                  <p className="text-[10px] text-muted-foreground">
                    Leave empty to keep the existing key unchanged.
                  </p>
                )}
              </div>
            )}

            {/* Custom Headers */}
            {authType === 'custom_headers' && (
              <div className="border-t">
                <div className="px-3 py-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium">Headers</Label>
                    <span className="text-[10px] text-muted-foreground">{customHeaders.length} header{customHeaders.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="space-y-2">
                    {customHeaders.map((header, idx) => (
                      <div key={idx} className="flex gap-1.5">
                        <Input
                          value={header.key}
                          onChange={(e) => updateHeader(idx, 'key', e.target.value)}
                          placeholder="Header name"
                          className="text-xs h-8 font-mono flex-[2]"
                        />
                        <Input
                          value={header.value}
                          onChange={(e) => updateHeader(idx, 'value', e.target.value)}
                          placeholder="Value"
                          type="password"
                          className="text-xs h-8 font-mono flex-[3]"
                        />
                        <button
                          type="button"
                          onClick={() => removeHeader(idx)}
                          className="size-8 shrink-0 flex items-center justify-center rounded-md border border-input text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addHeader}
                    className="w-full flex items-center justify-center gap-1.5 h-8 rounded-lg border border-dashed border-input text-xs text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                  >
                    <Plus className="size-3" />
                    Add header
                  </button>
                  <p className="text-[10px] text-muted-foreground">
                    Headers are sent with every request to the MCP server. Values are stored securely.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-muted/30">
        <Button variant="outline" size="sm" onClick={onBack} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              {isEditing ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <Check className="size-3.5 mr-1.5" />
              {isEditing ? 'Save Changes' : 'Create Connector'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
